/**
 * Progressive distortion of an existing chart.
 *
 * Unlike Tweak (calm whole-grid geometry), Distort mutates the repeating
 * motif tile itself, then retires it — so each Apply further degrades the
 * pattern while the result stays a clean, tileable repeat.
 *
 * `level` (1–5) controls how hard one click hits: 1 is a single mild change.
 */

import {
  colorsToLetters,
  detectColPeriod,
  detectRowPeriod,
  normalizePatternGrid,
  patternHasContent,
} from "./tweakPattern";

export const MIN_DISTORT_LEVEL = 1;
export const MAX_DISTORT_LEVEL = 5;
export const DEFAULT_DISTORT_LEVEL = 1;

export const DISTORT_LEVELS = [
  { id: 1, label: "1 — Subtle", detail: "One small local change" },
  { id: 2, label: "2 — Light", detail: "A couple of gentle wounds" },
  { id: 3, label: "3 — Medium", detail: "Noticeable shear and swaps" },
  { id: 4, label: "4 — Strong", detail: "Several aggressive mutations" },
  { id: 5, label: "5 — Heavy", detail: "Heavy motif degradation" },
];

export function clampDistortLevel(level) {
  const n = Math.floor(Number(level));
  if (!Number.isFinite(n)) return DEFAULT_DISTORT_LEVEL;
  return Math.max(MIN_DISTORT_LEVEL, Math.min(MAX_DISTORT_LEVEL, n));
}

function randInt(rng, min, max) {
  return min + Math.floor(rng() * (max - min + 1));
}

function pick(rng, list) {
  return list[randInt(rng, 0, list.length - 1)];
}

function toRows(grid, cols, rows) {
  const out = [];
  for (let r = 0; r < rows; r += 1) {
    out.push(grid.slice(r * cols, r * cols + cols));
  }
  return out;
}

function rotate(arr, by) {
  const n = arr.length;
  if (n === 0) return arr.slice();
  const k = ((by % n) + n) % n;
  if (k === 0) return arr.slice();
  return arr.slice(k).concat(arr.slice(0, k));
}

/** Pull the top-left motif tile (colPeriod × rowPeriod). */
export function extractMotif(rowGrid, colPeriod, rowPeriod) {
  const tile = [];
  for (let r = 0; r < rowPeriod; r += 1) {
    tile.push(rowGrid[r].slice(0, colPeriod));
  }
  return tile;
}

/** Paint the motif across the full chart. */
export function tileMotif(motif, cols, rows) {
  const mh = motif.length;
  const mw = mh > 0 ? motif[0].length : 0;
  if (mh < 1 || mw < 1) {
    return Array.from({ length: cols * rows }, () => null);
  }
  const out = [];
  for (let r = 0; r < rows; r += 1) {
    const src = motif[r % mh];
    for (let c = 0; c < cols; c += 1) {
      out.push(src[c % mw]);
    }
  }
  return out;
}

function motifCellCount(motif) {
  if (!motif.length) return 0;
  return motif.length * motif[0].length;
}

function cloneMotif(motif) {
  return motif.map((row) => row.slice());
}

/**
 * How many local mutations one Distort click applies.
 * Level 1 is always a single change; higher levels add more (and scale a
 * bit with motif size so large tiles can actually look heavy).
 */
export function distortStepCount(motifCells, level = DEFAULT_DISTORT_LEVEL) {
  const cells = Math.max(1, Math.floor(Number(motifCells)) || 1);
  const lv = clampDistortLevel(level);
  if (lv === 1) return 1;
  const sizeBoost = Math.floor(Math.sqrt(cells) * 0.3 * (lv - 1));
  return Math.min(cells, lv + sizeBoost);
}

/** Milder ops at low levels; stronger geometry at higher levels. */
export function distortOpsForLevel(level = DEFAULT_DISTORT_LEVEL) {
  const lv = clampDistortLevel(level);
  const ops = [swapAdjacent, bleedNeighbor];
  if (lv >= 2) ops.push(rowJitter, colJitter);
  if (lv >= 4) ops.push(swapRandomPair);
  return ops;
}

function swapAdjacent(motif, rng) {
  const h = motif.length;
  const w = motif[0].length;
  if (h * w < 2) return { skipped: true };

  const r = randInt(rng, 0, h - 1);
  const c = randInt(rng, 0, w - 1);
  const dirs = [];
  if (w > 1) dirs.push([0, 1], [0, -1]);
  if (h > 1) dirs.push([1, 0], [-1, 0]);
  if (dirs.length === 0) return { skipped: true };

  const [dr, dc] = pick(rng, dirs);
  const r2 = (r + dr + h) % h;
  const c2 = (c + dc + w) % w;
  if (r2 === r && c2 === c) return { skipped: true };

  const a = motif[r][c];
  motif[r][c] = motif[r2][c2];
  motif[r2][c2] = a;
  return { type: "swapAdjacent", r, c, r2, c2 };
}

/** Copy a neighbor onto this cell — softens edges / bleeds color. */
function bleedNeighbor(motif, rng) {
  const h = motif.length;
  const w = motif[0].length;
  if (h * w < 2) return { skipped: true };

  const r = randInt(rng, 0, h - 1);
  const c = randInt(rng, 0, w - 1);
  const dirs = [];
  if (w > 1) dirs.push([0, 1], [0, -1]);
  if (h > 1) dirs.push([1, 0], [-1, 0]);
  if (dirs.length === 0) return { skipped: true };

  const [dr, dc] = pick(rng, dirs);
  const r2 = (r + dr + h) % h;
  const c2 = (c + dc + w) % w;
  if (motif[r][c] === motif[r2][c2]) return { skipped: true };

  motif[r][c] = motif[r2][c2];
  return { type: "bleed", r, c, fromR: r2, fromC: c2 };
}

/** Shear one motif row by ±1 within the tile. */
function rowJitter(motif, rng) {
  const h = motif.length;
  const w = motif[0].length;
  if (w < 2) return { skipped: true };
  const r = randInt(rng, 0, h - 1);
  const amount = rng() < 0.5 ? -1 : 1;
  motif[r] = rotate(motif[r], amount);
  return { type: "rowJitter", r, amount };
}

/** Shear one motif column by ±1 within the tile. */
function colJitter(motif, rng) {
  const h = motif.length;
  const w = motif[0].length;
  if (h < 2) return { skipped: true };
  const c = randInt(rng, 0, w - 1);
  const amount = rng() < 0.5 ? -1 : 1;
  const col = motif.map((row) => row[c]);
  const shifted = rotate(col, amount);
  for (let r = 0; r < h; r += 1) motif[r][c] = shifted[r];
  return { type: "colJitter", c, amount };
}

function swapRandomPair(motif, rng) {
  const h = motif.length;
  const w = motif[0].length;
  const n = h * w;
  if (n < 2) return { skipped: true };
  const i = randInt(rng, 0, n - 1);
  let j = randInt(rng, 0, n - 2);
  if (j >= i) j += 1;
  const r1 = Math.floor(i / w);
  const c1 = i % w;
  const r2 = Math.floor(j / w);
  const c2 = j % w;
  if (motif[r1][c1] === motif[r2][c2]) return { skipped: true };
  const tmp = motif[r1][c1];
  motif[r1][c1] = motif[r2][c2];
  motif[r2][c2] = tmp;
  return { type: "swapPair", r1, c1, r2, c2 };
}

function motifUnchanged(a, b) {
  if (a.length !== b.length) return false;
  for (let r = 0; r < a.length; r += 1) {
    if (a[r].length !== b[r].length) return false;
    for (let c = 0; c < a[r].length; c += 1) {
      if (a[r][c] !== b[r][c]) return false;
    }
  }
  return true;
}

/**
 * Distort the repeating motif of an existing pattern.
 * Each call mutates further from the current chart (progressive degradation).
 * `level` 1–5: 1 = one minor change; higher = more / stronger mutations.
 * @returns {null | { pattern, letters, letterToColor, meta }}
 */
export function distortPattern({
  pattern,
  cols,
  rows,
  defaultColor = "#e6e6e6",
  rng = Math.random,
  level = DEFAULT_DISTORT_LEVEL,
  // Back-compat alias for older callers.
  intensity,
}) {
  if (!patternHasContent(pattern, defaultColor)) return null;

  const distortLevel = clampDistortLevel(
    intensity != null ? intensity : level
  );

  const { grid: start, cols: width, rows: height } = normalizePatternGrid(
    pattern,
    cols,
    rows,
    defaultColor
  );
  const rowGrid = toRows(start, width, height);
  const rowPeriod = detectRowPeriod(rowGrid);
  const colPeriod = detectColPeriod(rowGrid);
  const sourceMotif = extractMotif(rowGrid, colPeriod, rowPeriod);
  const motif = cloneMotif(sourceMotif);
  const steps = distortStepCount(motifCellCount(motif), distortLevel);
  const opFns = distortOpsForLevel(distortLevel);
  const ops = [];

  // Prefer level-appropriate wounds; fall back across the pool.
  for (let i = 0; i < steps; i += 1) {
    const pool = opFns.slice();
    let applied = false;
    while (pool.length > 0 && !applied) {
      const idx = randInt(rng, 0, pool.length - 1);
      const op = pool.splice(idx, 1)[0];
      const before = cloneMotif(motif);
      const result = op(motif, rng);
      if (!result.skipped && !motifUnchanged(before, motif)) {
        ops.push(result);
        applied = true;
      } else {
        // Revert a skipped/no-op mutate (ops mutate in place).
        for (let r = 0; r < motif.length; r += 1) {
          motif[r] = before[r].slice();
        }
      }
    }
  }

  // Ops can cancel (swap then swap back). Force a visible change when needed.
  if (motifUnchanged(sourceMotif, motif) && motifCellCount(motif) >= 2) {
    const forced = swapAdjacent(motif, () => 0.1);
    if (!forced.skipped && !motifUnchanged(sourceMotif, motif)) {
      ops.push(forced);
    } else {
      // Restore then try a different seat / pair.
      for (let r = 0; r < motif.length; r += 1) {
        motif[r] = sourceMotif[r].slice();
      }
      const pair = swapRandomPair(motif, () => 0.25);
      if (!pair.skipped && !motifUnchanged(sourceMotif, motif)) {
        ops.push(pair);
      }
    }
  }

  if (ops.length === 0 || motifUnchanged(sourceMotif, motif)) {
    // Single-cell / solid motif: nothing to degrade.
    return null;
  }

  const grid = tileMotif(motif, width, height);
  const { letters, letterToColor } = colorsToLetters(grid, defaultColor);
  return {
    pattern: grid,
    letters,
    letterToColor,
    meta: {
      style: "distort",
      level: distortLevel,
      ops,
      rowPeriod,
      colPeriod,
      steps: ops.length,
      colorCount: Object.keys(letterToColor).length,
    },
  };
}
